/**
 * LiDAR Probe Room tool — experimental room detection by ray probing.
 *
 * The MAP is deliberately plain — none of the wall tracer's band
 * machinery: a clipped density capture of the viewport, depth-filtered by
 * the position map to wall and door/window-head heights (everything else
 * is not boundary), with the RAW scanned/void border overlaid to stand in
 * for walls the sweep never scanned, and small isolated islands removed
 * last. The SHAPE comes from ray casting: probes dropped inside the room
 * fan rays out until they hit that map, and the hits are the boundary.
 *
 * Interaction is a LiDAR sweep. Click once to probe from a single point, or
 * drag the cursor through the room to probe from every position along the
 * path — an L-shaped room no single point can see in full is covered by
 * sweeping the cursor through it. All probes' hits merge into one path.
 * Releasing the drag (or the single click) hands the cleaned polygon to the
 * hosted LineTool as edge prompts — the same magic-room edit flow the wall
 * tracer uses (draggable line prompts, Enter creates the room via the
 * adapter's commitNew, Esc discards) — then returns to probing. With the
 * "Enter to confirm" setting on, an overview step comes first: the release
 * freezes the probed shape + its probe origins on screen. Further clicks
 * and drags ADD probes to the sweep, right-clicking an origin removes it,
 * and Enter then opens the edit session (Escape discards everything).
 * Every probe carries a hit-exclusion disk (white ring) stamped out of the
 * cast mask — rays fly through it and stop on what's behind, so placing a
 * probe on a door and growing its ring (hover + scroll; the wheel is
 * swallowed so the camera stays put) paints the door out of the sweep.
 * Committed probes are world-anchored and never re-cast on camera moves —
 * pan, sweep more, and the combined shape can outgrow the screen.
 *
 * Rays that escape through a door opening or a hole in the scan fly on into
 * the next room and would drape the polygon around it. Those are discarded
 * before the shape is built (see `rejectOutliers` in the probe module): a
 * hit is trusted only when its distance agrees with its angular neighbours,
 * and a short run of escapees is bridged straight across — which is exactly
 * the chord a doorway should contribute to a room boundary.
 */

import * as THREE from 'three';
import { Line2 } from 'three/examples/jsm/lines/Line2.js';
import { LineGeometry } from 'three/examples/jsm/lines/LineGeometry.js';
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js';
import { Viewer } from '../../../viewer/viewer';
import { IPersona } from '../../types';
import { FloorplanToolBase } from './FloorplanToolBase';
import { DragEventData } from '../../BaseTool';
import { CoordinateTransformer } from '../modules/utils/CoordinateTransformer';
import { ImageCapture } from '../modules/capture/ImageCapture';
import { PolygonProcessor } from '../../../utils/PolygonProcessor';
import { createPolygon, Point2D } from '../../../utils/segmentation/types';
import { PipelineControlPanel, PipelineInterface } from '../../../ui/pipeline/PipelineControlPanel';
import { PointoramaRoom } from '../../../utils/PointoramaRoom';
import { PolygonRenderer3D } from '../modules/segmentation/PolygonRenderer3D';
import { SphereMarker } from '../../../utils/SphereMarker';
import { TracerMask, TracerPoint } from '../modules/wallTracer/snakeTracer';
import { ProbeOptions, DEFAULT_PROBE_OPTIONS, RayHit, ProbeFan, ProbeHit, probeFan, buildRing, carveThinOccluders, thinPath, removeSmallBlobs } from '../modules/lidarProbe/lidarProbe';
import { MaskPrepOptions, MaskStageSink, DEFAULT_MASK_PREP_OPTIONS, composeWorkingMask, dilateMask } from '../modules/lidarProbe/maskComposition';
import { DepthSegmentationPipeline } from '../../../utils/segmentation/DepthSegmentationCore';
import { SubToolHosting } from '../../SubToolHosting';
import { SubToolInputKind } from '../../SubTool';
import { LineTool } from './LineTool';
import { RoomKindAdapter } from './line-tool/RoomKindAdapter';
import type { CommitContext } from './line-tool/AnnotationKindAdapter';
import { LinePrompt } from '../modules/line-detection/types';
import { MaskToolDebugKit, downloadFile } from '../widgets/MaskToolDebugKit';

/** Settings baked into the map at capture time — changing one recaptures
 * (everything else refreshes cheaper; see applySetting). */
const CAPTURE_BAKED_SETTINGS = new Set(['sealOpenings', 'wallDepthValue', 'wallDepthTolerance', 'doorDepthMin', 'doorDepthMax', 'closeRadiusPx', 'skeletonize', 'spurMaxPx', 'holeFillPx', 'postProcess', 'clipMin']);

/**
 * The on-disk settings config — tuned values exported from the debug
 * panel land here (apps/client/public/config/segmentation/) to become
 * the baseline everyone loads. Every field is optional: missing keys
 * ride on the compiled defaults, so a partial file is valid. The fixture
 * harness reads the same file so replays match the tool.
 */
interface LidarProbeSettingsFile {
  prep?: Partial<MaskPrepOptions>;
  probe?: Partial<ProbeOptions>;
  postProcess?: boolean;
  enterToConfirm?: boolean;
  showRays?: boolean;
}

const SETTINGS_CONFIG_PATH = '/config/segmentation/lidar-probe-settings.json';

/** Brand green (neon-green-300) — a usable probed shape. */
const SHAPE_COLOR = 0xb5ffa1;
/** Orange — probed, but too few hits survived to trust the shape. */
const WEAK_COLOR = 0xffaa00;
/** Red — a ray discarded as an escape through a door or hole (debug view). */
const REJECTED_RAY_COLOR = 0xff4444;
/** Blue-ish — a ray that hit a wall and was kept (debug view). */
const KEPT_RAY_COLOR = 0x4499ff;

/** Padding (px) around committed rooms punched out of the capture. */
const ROOM_PUNCH_PAD_PX = 10;
/** Spacing (mask px) between probe origins taken from a drag path. */
const PROBE_SPACING_PX = 25;
/** Below this many surviving hits the shape is drawn as untrustworthy. */
const MIN_TRUSTED_HITS = 12;
/** Screen-distance (px) for right-clicking a probe origin to remove it. */
const PROBE_PICK_PX = 12;
/** Default radius (px) of the hit-exclusion disk around each probe. The
 * disk is stamped black into the cast mask, so rays fly through it and
 * stop on what's behind — hover a committed probe and scroll to resize. */
const PROBE_EXCLUSION_PX = 20;

/**
 * A committed probe, stored in WORLD space so camera pans/zooms never
 * re-cast it: its fan was judged against the capture it was probed in, and
 * only the geometry is carried forward. That is what lets a sweep grow a
 * room larger than one screen — hits scrolled out of view keep bounding
 * the shape from off-canvas coordinates.
 */
interface CommittedProbe {
  originWorld: THREE.Vector3;
  /** Radius (world meters) of the probe's hit-exclusion disk — the area
   * stamped out of the cast mask so rays pass through. Wheel-resizable. */
  exclusionRadiusWorld: number;
  /** Endpoints of the rays that found a wall, with rejection verdicts —
   * feeds the occlusion test and the debug ray overlay on rebuild. */
  raysWorld: Array<{ end: THREE.Vector3; rejected: boolean }>;
  /** Hits surviving rejection + bridging — this fan's boundary share. */
  hitsWorld: THREE.Vector3[];
  /** Per-hit weak flags, parallel to `hitsWorld`: true for wide-opening
   * escapee hits (see ProbeHit in lidarProbe). Preserved across commits so
   * a rebuilt fan's hits keep their provenance — group filtering treats
   * weak hits as second-class, and losing the flag would let a corridor
   * track harden into "boundary" on the next rebuild. */
  hitsWeak: boolean[];
}

/**
 * Room adapter for the LiDAR probe host — same construction as the wall
 * tracer's: identical room semantics (hit-testing, commit, floor context)
 * but PURE-GEOMETRY editing only. `enableLineDetection = false` is the
 * modes' sanctioned opt-out that skips depth segmentation, line detection
 * and the region machinery entirely; `captureMapsForSnapping` keeps the
 * viewport maps flowing so density snapping works while dragging prompts.
 */
class LidarProbeRoomAdapter extends RoomKindAdapter {
  /** From the adapter interface (not declared on RoomKindAdapter itself). */
  readonly enableLineDetection = false;
  /** …but keep capturing viewport maps so density snapping works in edits. */
  readonly captureMapsForSnapping = true;

  /**
   * Set by the host during a TEST-fixture session: `commitNew` (which the
   * mode only calls on the final shape confirm — intermediate Enter
   * confirms never reach it) hands the polygon to the fixture instead of
   * creating a room. Same construction as the wall tracer's adapter.
   */
  commitInterceptor: ((polygon: THREE.Vector3[]) => void) | null = null;

  override shouldRunSegmentationOnEnter(): boolean {
    return false;
  }

  override commitNew(viewer: Viewer, polygon: THREE.Vector3[], context: CommitContext): void {
    if (this.commitInterceptor) {
      this.commitInterceptor(polygon.map((p) => p.clone()));
      return;
    }
    super.commitNew(viewer, polygon, context);
  }
}

export class LidarProbeRoomTool extends SubToolHosting(FloorplanToolBase) {
  public name = 'lidar probe room';

  private coordinateTransformer: CoordinateTransformer;
  private imageCapture: ImageCapture;
  private polygonProcessor: PolygonProcessor;
  /** Renders the probed polygon exactly like the magic room tool's preview
   * (green fill + purple outline + vertex dots). */
  private polygonRenderer: PolygonRenderer3D;

  /**
   * Hosted LineTool + room adapter: a committed probe hands the polygon to
   * the same edit machinery the magic room tool uses (draggable line
   * prompts, confirm/cancel), then returns to probing.
   */
  private readonly lineTool: LineTool;
  private readonly roomAdapter: LidarProbeRoomAdapter;
  /** True while the hosted LineTool owns input for a room-edit session. */
  private roomEditing = false;
  /**
   * Set synchronously when a commit hands off to the edit session. BaseTool
   * fires onDragEnd AND onMouseUp for the same release, and the session
   * entry is async — without this flag the trailing mouse-up would commit a
   * second single-point probe while the session is still starting.
   */
  private editSessionPending = false;
  /** Tool option: releasing a probe freezes it as an overview (shape +
   * probe origins) and Enter opens room edit; off opens room edit
   * immediately on release. */
  private enterToConfirm = false;
  /** The capture-prep parameters in effect (see MaskPrepOptions) — all
   * tunable live from the debug panel. Baked into the map at capture
   * time: changing any of them recaptures. */
  private prep: MaskPrepOptions = { ...DEFAULT_MASK_PREP_OPTIONS };
  /** Master toggle for the post-processing steps AFTER the clip
   * threshold — the skeleton/shell reduction, spur pruning and
   * small-blob removal. Off casts against the plain closed map: the A/B
   * view for tuning the steps against the stage images. */
  private postProcess = true;
  /** Pixels sealed as openings in the current capture (mask indices). */
  private lastSealed: number[] = [];
  /** Baked overlay of `lastSealed` for the debug canvas (the blue masks). */
  private sealOverlay: HTMLCanvasElement | null = null;
  /** Hit-reject mask for the current capture: the opening zones dilated by
   * the hit margin, with the void border collar exempted. Any ray hit
   * landing inside it is treated as "found nothing". */
  private openingHitReject: Uint8Array | null = null;
  /** True while a probe overview is frozen on screen (Enter opens edit). */
  private committed = false;
  /** The overview's accumulated probes, world-anchored (see CommittedProbe).
   * Additional clicks/drags append; right-clicking an origin removes it. */
  private committedProbes: CommittedProbe[] = [];
  /** BaseTool fires onDragEnd AND onMouseUp for the same release; this
   * keeps the trailing mouse-up from adding an extra single-point probe. */
  private dragJustEnded = false;
  /** The thinned origins of the LAST drag preview — the release commits
   * exactly these, so what was previewed is what gets confirmed. */
  private lastDragOrigins: TracerPoint[] = [];

  /** Pristine capture (rooms not yet punched out). */
  private baseMask: TracerMask | null = null;
  /** The RAW density screenshot the composition ran on — saved into
   * fixtures so the harness can recompose the working map from scratch
   * with the CURRENT prep defaults (making them tunable in tests). */
  private rawCapture: TracerMask | null = null;
  /** Working capture: `baseMask` minus committed rooms. */
  private mask: TracerMask | null = null;
  private capturing = false;
  /** Settle timer: recapture once the camera stops moving. */
  private recaptureTimer: number | null = null;
  /** True from the first camera move until the next capture completes:
   * the map on screen is stale, so the cursor spins, the preview is
   * cleared and probing pauses. */
  private mapStale = false;
  /** The capture's position map, kept for the fixture export — pixel
   * alignment needs it, and it cannot round-trip through a canvas PNG
   * (the 12-bit XY low bits live in alpha, which PNG premultiplies). */
  private lastPositionMap: ImageData | null = null;
  /** What the hosted edit session is for: 'draft' commits a room; 'test'
   * intercepts the final confirm and saves it as a fixture's EXPECTED
   * polygon (same flow as the wall tracer's test capture). */
  private roomSessionKind: 'draft' | 'test' = 'draft';
  /** Fixture data frozen when a test capture starts — the edit session
   * resets the live probe state, so everything is snapshotted first. */
  private testSnapshot: {
    origins: TracerPoint[];
    mask: TracerMask;
    /** The RAW density capture — the harness recomposes the working map
     * from this with the CURRENT prep defaults (parameters stay tunable). */
    raw: TracerMask | null;
    positionMap: ImageData | null;
    pixelsPerMeter: number | null;
    prep: MaskPrepOptions;
  } | null = null;

  private probeOptions: ProbeOptions = { ...DEFAULT_PROBE_OPTIONS };

  /** The capture-prep parameters currently in effect. The post-process
   * master toggle gates the reduction steps (skeleton/shell) without
   * losing their tuned values. */
  private maskPrep(): MaskPrepOptions {
    if (this.postProcess) return { ...this.prep };
    return { ...this.prep, skeletonize: false, shellPx: 0 };
  }

  /** Probe origins of the sweep in progress (mask px), or the committed one. */
  private origins: TracerPoint[] = [];
  /** Raw drag path before thinning (mask px). */
  private dragPath: TracerPoint[] = [];
  private dragging = false;
  /** The probed boundary (mask px), cleaned; null when nothing is probed. */
  private shape: TracerPoint[] | null = null;
  /** Rays of the last probe, kept for the debug ray overlay. */
  private lastRays: RayHit[] = [];
  /** Hits the filters discarded, kept for the debug overlay. */
  private lastDiscarded: TracerPoint[] = [];
  /** Pixels the thin-occluder carve erased in the last probe run (mask
   * indices), for the debug overlay. */
  private lastCarved: number[] = [];
  /** Baked overlay of `lastCarved` for the debug canvas. */
  private carveOverlay: HTMLCanvasElement | null = null;

  /** Markers on the committed sweep's probe origins (overview step). */
  private originMarkers: SphereMarker[] = [];
  /** World-anchored circles showing each probe's exclusion radius. */
  private originRings: THREE.LineLoop[] = [];
  /** Debounce for re-probing after wheel radius tweaks (the ring visual
   * tracks every tick; the re-cast is too heavy per wheel notch). */
  private radiusReapplyTimer: number | null = null;

  private previewLine: Line2 | null = null;
  private previewGeometry: LineGeometry | null = null;
  private previewMaterial: LineMaterial | null = null;
  private rayGroup: THREE.Group | null = null;
  /** Debug option: draw every ray, rejected ones in red. */
  private showRays = false;

  /** Monotonic token so stale async cleanups never overwrite newer previews. */
  private previewToken = 0;

  // Debug widget: the THRESHOLDED map the rays actually read, the raw
  // probed ring and the cleaned polygon on top of it, plus the polygon
  // pipeline's live controls.
  private debugContainer: HTMLDivElement | null = null;
  private debugCanvas: HTMLCanvasElement | null = null;
  /** The thresholded map baked once per capture / threshold change. */
  private debugBase: HTMLCanvasElement | null = null;
  private pipelinePanel: PipelineControlPanel | null = null;
  /** Shared debug-panel machinery (stage images, settings UI primitives,
   * JSON config load/export), parameterized with THIS tool's config file. */
  private readonly debugKit = new MaskToolDebugKit({ configPath: SETTINGS_CONFIG_PATH, exportFilename: 'lidar-probe-settings.json' });
  /** The debug-panel settings controls, replaced wholesale when the JSON
   * settings config loads over an already-built panel. */
  private settingsControlsRoot: HTMLElement | null = null;
  /** Probed ring before the polygon pipeline (debug overlay). */
  private lastRawRing: TracerPoint[] | null = null;
  /** Last cleanAndRender input, so pipeline edits can live-refresh the shape. */
  private lastCleanInput: { ring: TracerPoint[]; color: number } | null = null;

  constructor(viewer: Viewer, persona: IPersona) {
    super(viewer, persona);
    this.coordinateTransformer = new CoordinateTransformer(viewer);
    this.imageCapture = new ImageCapture(viewer, this.coordinateTransformer);
    this.polygonRenderer = new PolygonRenderer3D(viewer, this.coordinateTransformer);
    // Same passes the wall tracer cleans its loops with, but its OWN config
    // file: a probed ring carries one vertex per ray, far noisier than a
    // traced path, so it starts at a stronger simplification — and the two
    // tools' debug panels must not edit each other's pipeline.
    this.polygonProcessor = new PolygonProcessor({ configPath: '/config/segmentation/lidar-probe-poly-pipeline-config.json' });
    void this.loadSettingsConfig();

    // Same wiring as the wall tracer: the hosted LineTool edits the probed
    // polygon; the mixin routes input to it, but only while a room edit
    // session is active (all input kinds are muted outside sessions).
    this.roomAdapter = new LidarProbeRoomAdapter();
    this.lineTool = new LineTool(viewer, persona, undefined, this.roomAdapter);
    const ctx = this.roomAdapter.createSelectionContext(viewer, persona);
    this.initSubToolHosting(this.lineTool, this.roomAdapter, ctx);
  }

  /** Route subtool input only during a room-edit session. */
  private setRoomEditRouting(active: boolean): void {
    const kinds: SubToolInputKind[] = ['pointer', 'click', 'keyboard', 'wheel'];
    for (const kind of kinds) this.muteInput(kind, !active);
    this.roomEditing = active;
  }

  /**
   * A forwarded event may have ended the edit session (Enter created the
   * room, Esc discarded, prompts deleted). Detect that, fall back to probe
   * mode and refresh the working mask — a created room must be re-punched
   * out of the capture.
   */
  private syncRoomEditState(): void {
    if (!this.roomEditing) return;
    const mode = this.lineTool.getActiveMode();
    if (mode.getLinePrompts().length > 0) return;
    if (this.roomSessionKind === 'test') {
      this.roomAdapter.commitInterceptor = null;
      this.roomSessionKind = 'draft';
      // Ended WITHOUT a shape confirm (Esc / prompts deleted): still save
      // the fixture — invariants-only, no expected polygon.
      if (this.testSnapshot) {
        this.writeProbeFixture(null);
        this.testSnapshot = null;
      }
    }
    this.setRoomEditRouting(false);
    // Back to probe mode: the edit session's leftover line-prompt
    // suggestions must not linger (in the magic room tool they'd seed the
    // next draw; here the probe owns the canvas again).
    mode.setLinePrompts([]);
    mode.updateShapePreview();
    mode.clearDetectionVisuals();
    this.lineTool.visualizer.clearPromptVisuals();
    this.applyRoomPunches();
  }

  /** The subtool edits on the floor plane — re-push it on floor switches. */
  protected onFloorChanged(): void {
    this.pushBasePlane();
  }

  protected onEnable(): void {
    super.onEnable();
    this.setRoomEditRouting(false);
    this.reset();
    this.createDebugWidget();
    this.viewer.addEventListener('camera_changed', this.handleCameraChange);
    this.viewer.addEventListener('controls_changed', this.handleCameraChange);
    this.viewer.scene.addEventListener('room_added', this.handleRoomsChanged);
    this.viewer.scene.addEventListener('room_removed', this.handleRoomsChanged);
    this.viewer.scene.addEventListener('room_updated', this.handleRoomsChanged);
    this.captureView();
  }

  protected onDisable(): void {
    this.setRoomEditRouting(false);
    this.viewer.removeEventListener('camera_changed', this.handleCameraChange);
    this.viewer.removeEventListener('controls_changed', this.handleCameraChange);
    this.viewer.scene.removeEventListener('room_added', this.handleRoomsChanged);
    this.viewer.scene.removeEventListener('room_removed', this.handleRoomsChanged);
    this.viewer.scene.removeEventListener('room_updated', this.handleRoomsChanged);
    this.setMapStale(false);
    if (this.recaptureTimer !== null) {
      window.clearTimeout(this.recaptureTimer);
      this.recaptureTimer = null;
    }
    if (this.radiusReapplyTimer !== null) {
      window.clearTimeout(this.radiusReapplyTimer);
      this.radiusReapplyTimer = null;
    }
    this.reset();
    this.destroyDebugWidget();
    this.mask = null;
    this.baseMask = null;
    this.rawCapture = null;
    this.lastSealed = [];
    this.sealOverlay = null;
    this.openingHitReject = null;
    this.lastPositionMap = null;
    super.onDisable();
  }

  /** Rooms changed — the punched-out areas of the working mask are stale. */
  private handleRoomsChanged = (): void => {
    if (!this.enabled) return;
    this.applyRoomPunches();
  };

  /**
   * Camera pan/zoom invalidates the capture. Debounced: recapture once the
   * movement settles instead of re-rendering every frame.
   */
  private handleCameraChange = (): void => {
    if (!this.enabled) return;
    // camera_changed / controls_changed also fire on NO-OP ticks (control
    // damping, numeric jitter) — declaring the map stale on those aborts a
    // drag and clears the preview for nothing. Only a viewport that
    // actually maps to different world corners is stale.
    if (this.viewportMatchesCapture()) return;
    // The capture is stale the MOMENT the camera really moves: spinner
    // cursor on, preview cleared, probing paused — until the recapture
    // lands. (Not during an edit session, which owns the canvas and its
    // own visuals.)
    if (!this.roomEditing) this.setMapStale(true);
    if (this.recaptureTimer !== null) window.clearTimeout(this.recaptureTimer);
    this.recaptureTimer = window.setTimeout(() => {
      this.recaptureTimer = null;
      // The movement may have settled back exactly where the capture was
      // taken (e.g. a cancelled gesture) — then the map is valid again.
      if (this.viewportMatchesCapture()) {
        this.setMapStale(false);
        return;
      }
      this.captureView();
    }, 250);
  };

  /** True when the current viewport maps to the same world corners the
   * active capture was taken from. */
  private viewportMatchesCapture(): boolean {
    const width = this.viewer.renderArea.clientWidth;
    const height = this.viewer.renderArea.clientHeight;
    const cornerTL = this.coordinateTransformer.screenToWorld(new THREE.Vector2(0, 0));
    const cornerBR = this.coordinateTransformer.screenToWorld(new THREE.Vector2(width, height));
    const stored = this.coordinateTransformer.getMaskCorners();
    return !!cornerTL && !!cornerBR && stored.length === 4 && stored[0].distanceTo(cornerTL) < 0.001 && stored[2].distanceTo(cornerBR) < 0.001;
  }

  /**
   * Stale = the map on screen no longer matches the camera. While stale:
   * a spinner (wait) cursor, no preview, no probing — everything resumes
   * when the next capture completes (captureView clears the flag).
   */
  private setMapStale(stale: boolean): void {
    if (this.mapStale === stale) return;
    this.mapStale = stale;
    this.viewer.renderer.domElement.style.cursor = stale ? 'wait' : '';
    if (stale) {
      this.previewToken++; // drop in-flight preview renders
      // A drag in progress is aborted — its path is in the old capture's
      // pixel space and cannot be carried across.
      this.dragging = false;
      this.dragPath = [];
      this.clearPreview();
    }
  }

  // ─── Capture ───────────────────────────────────────────────────────────

  /**
   * Build the probe map: the wall tracer's capture pipeline (1:1 viewport
   * density shot → morphological close → void band), read later at the
   * probe's higher threshold. The threshold is applied per ray rather than
   * baked in, so changing it re-probes without recapturing.
   */
  private captureView(): void {
    if (this.capturing) return;
    this.capturing = true;
    try {
      const width = this.viewer.renderArea.clientWidth;
      const height = this.viewer.renderArea.clientHeight;
      const shot = this.viewer.screenshotRenderer.renderScreenshot(new THREE.Vector2(0, 0), width, height, false, 1.0, true);
      const pixelWidth = Math.round(shot.pixelWidth);
      const pixelHeight = Math.round(shot.pixelHeight);
      if (shot.buffer.length !== pixelWidth * pixelHeight * 4) return;

      const screenCorners = [new THREE.Vector2(0, 0), new THREE.Vector2(width, 0), new THREE.Vector2(width, height), new THREE.Vector2(0, height)];
      const worldCorners: THREE.Vector3[] = [];
      for (const corner of screenCorners) {
        const world = this.coordinateTransformer.screenToWorld(corner);
        if (!world) return;
        worldCorners.push(world);
      }
      this.coordinateTransformer.setMaskCorners(worldCorners);
      this.coordinateTransformer.setCapturedImage({ canvas: null, width: pixelWidth, height: pixelHeight });

      const pxPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();

      // Feed the polygon processor everything its passes may need (pixel
      // alignment wants the position map + density mask + real scale). The
      // position map's depth encoding needs the floor→ceiling z-range, or
      // it comes back degenerate (all-zero) and pixel alignment yields NaN.
      // Captured BEFORE the mask is composed — the wall depth filter below
      // reads its depth channel.
      this.polygonProcessor.setPixelsPerMeter(pxPerMeter ?? 1);
      const floorZForMap = this.currentFloorFloorPlane?.height ?? 0;
      const ceilingZForMap = this.currentFloorCeilingPlane?.height ?? floorZForMap + 3;
      const zRange: [number, number] = [Math.min(floorZForMap, ceilingZForMap) - 0.5, Math.max(floorZForMap, ceilingZForMap) + 0.5];
      const positionMap = this.imageCapture.capturePositionMap(new THREE.Vector2(0, 0), new THREE.Vector2(width, height), pixelWidth, pixelHeight, zRange);
      if (positionMap) {
        // Pixel alignment decodes WORLD positions out of the position map's
        // eye-space encoding — it needs the encoding metadata, or it reads
        // garbage depth and mis-snaps (or emits NaN). This mirrors the wall
        // tracer / LineDetectionService.captureViewportMaps; without it the
        // map's raw ImageData is meaningless to the alignment pass.
        this.coordinateTransformer.setPositionMapEncoding({
          eyeMin: positionMap.eyeMin,
          eyeMax: positionMap.eyeMax,
          inverseViewMatrix: positionMap.inverseViewMatrix,
        });
        this.polygonProcessor.setPositionMap(positionMap.imageData);
      }
      this.lastPositionMap = positionMap ? positionMap.imageData : null;

      // The whole composition lives in the shared maskComposition module —
      // the fixture harness runs the SAME code over the raw capture, so
      // every prep parameter stays tunable against fixtures. The raw shot
      // is kept for the fixture export.
      const raw: TracerMask = { data: new Uint8ClampedArray(shot.buffer), width: pixelWidth, height: pixelHeight };
      this.rawCapture = raw;
      // Intermediate stage images for the debug panel — only collected
      // while the widget exists (each snapshot is a full-size channel).
      this.debugKit.resetStages();
      const stageSink: MaskStageSink | undefined = this.debugContainer
        ? (stage, data): void => {
            this.debugKit.pushStage({ name: stage, data, width: pixelWidth, height: pixelHeight });
          }
        : undefined;
      const composed = composeWorkingMask(raw, positionMap ? positionMap.imageData : null, this.maskPrep(), this.probeOptions.clipMin, stageSink);
      this.lastSealed = composed.sealedIndices;
      this.sealOverlay = null;
      if (this.lastSealed.length > 0) this.rebuildSealOverlay(pixelWidth, pixelHeight);
      this.openingHitReject = composed.openingHitReject;
      this.baseMask = composed.mask;
      this.applyRoomPunches();

      // A frozen overview survives the recapture WITHOUT re-probing: the
      // committed fans are world-anchored, so they are merely re-expressed
      // in the new capture's pixel space and the ring is rebuilt. This is
      // what lets the user pan away and sweep the rest of a room larger
      // than the screen — hits now off-screen keep bounding the shape.
      if (this.committed && this.committedProbes.length > 0) {
        this.runProbe([]);
        this.renderOriginMarkers();
      }
    } finally {
      this.capturing = false;
      // The map matches the camera again — spinner off, probing resumes.
      this.setMapStale(false);
    }
  }


  /** Bake the sealed-opening overlay (blue) for the debug canvas. */
  private rebuildSealOverlay(width: number, height: number): void {
    this.sealOverlay = null;
    if (this.lastSealed.length === 0) return;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(width, height);
    for (const i of this.lastSealed) {
      img.data[i * 4] = 64;
      img.data[i * 4 + 1] = 200;
      img.data[i * 4 + 2] = 255;
      img.data[i * 4 + 3] = 200;
    }
    ctx.putImageData(img, 0, 0);
    this.sealOverlay = canvas;
  }

  /**
   * Re-stamp the working mask from the pristine capture with committed
   * rooms punched to black, so probing near an existing room cannot pick
   * that room's walls back up.
   */
  private applyRoomPunches(): void {
    if (!this.baseMask) return;
    const data = new Uint8ClampedArray(this.baseMask.data);
    const { width, height } = this.baseMask;

    const worldPolygons = this.getCommittedRoomPolygons();
    if (worldPolygons.length > 0) {
      const normalized = worldPolygons.map((polygon) =>
        polygon.map((point) => {
          const uv = this.coordinateTransformer.worldToMaskSpace(point);
          return { x: uv.x, y: uv.y };
        }),
      );
      const inside = DepthSegmentationPipeline.rasterizePolygonMask(normalized, width, height);
      const padded = dilateMask(inside, width, height, ROOM_PUNCH_PAD_PX);
      for (let i = 0; i < padded.length; i++) {
        if (padded[i] === 0) continue;
        data[i * 4] = 0;
        data[i * 4 + 1] = 0;
        data[i * 4 + 2] = 0;
      }
    }

    // Island removal LAST, judged on the final REDUCED map: solid
    // clutter has already collapsed to a thin ring or short skeleton by
    // now, so its small pixel count is exactly what lets it die here —
    // while a wall, a long line, keeps enough pixels to survive.
    // (`minBlobPx` must be sized for the reduction in use.) Part of the
    // post-processing master toggle: off skips the filter entirely.
    const punched: TracerMask = { data, width, height };
    this.mask = removeSmallBlobs(punched, this.probeOptions.clipMin, this.postProcess ? this.probeOptions.minBlobPx : 0);
    // Keep the processor's density mask in sync (pixel alignment probes it),
    // rooms included so alignment can't snap onto a punched-out zone.
    this.polygonProcessor.setDensityMask(new ImageData(new Uint8ClampedArray(this.mask.data), width, height));
    this.rebuildDebugBase();
    this.updateFinalStageImage();
  }

  /**
   * Refresh the final stage image (room punches + blob filter — the map
   * the rays actually cast against). Punches change without a recapture,
   * so this stage is maintained separately from the composition stages.
   */
  private updateFinalStageImage(): void {
    if (!this.debugContainer || !this.mask) return;
    this.debugKit.setFinalStage('punches + blob filter (final)', this.mask);
  }

  /**
   * World-space polygons of the rooms already committed on this floor —
   * the same set the magic-room adapter excludes (visible, on the active
   * floor group). Punching them out stops a probe near an existing room
   * from picking that room's far walls back up.
   */
  private getCommittedRoomPolygons(): THREE.Vector3[][] {
    const polygons: THREE.Vector3[][] = [];
    for (const room of this.viewer.scene.rooms as PointoramaRoom[]) {
      if (!room.visible) continue;
      if (room.groupIdentifier !== this.currentFloorGroupIdentifier) continue;
      const polygon = room.getPolygon();
      if (polygon.length < 3) continue;
      polygons.push(polygon);
    }
    return polygons;
  }

  // ─── Coordinate helpers ────────────────────────────────────────────────

  private eventToMaskPx(event: MouseEvent): TracerPoint | null {
    const rect = this.viewer.renderArea.getBoundingClientRect();
    return this.screenToMaskPx(new THREE.Vector2(event.clientX - rect.left, event.clientY - rect.top));
  }

  /** RenderArea-relative screen position → mask px (same space BaseTool's
   * `startScreen` / `currentScreen` drag coordinates are in). */
  private screenToMaskPx(screen: THREE.Vector2): TracerPoint | null {
    if (!this.mask) return null;
    const world = this.coordinateTransformer.screenToWorld(screen);
    if (!world) return null;
    let uv: THREE.Vector2;
    try {
      uv = this.coordinateTransformer.worldToMaskSpace(world);
    } catch {
      return null;
    }
    const x = uv.x * this.mask.width;
    const y = uv.y * this.mask.height;
    if (x < 0 || x >= this.mask.width || y < 0 || y >= this.mask.height) return null;
    return { x, y };
  }

  private maskPxToWorld(p: TracerPoint): THREE.Vector3 {
    const mask = this.mask;
    const u = mask ? p.x / mask.width : 0;
    const v = mask ? p.y / mask.height : 0;
    const world = this.coordinateTransformer.maskSpaceToWorld(new THREE.Vector2(u, v));
    world.z += 0.05;
    return world;
  }

  // ─── Input ─────────────────────────────────────────────────────────────

  public onMouseMove(event: MouseEvent): void {
    if (this.roomEditing) {
      super.onMouseMove(event);
      this.syncRoomEditState();
      return;
    }
    if (this.dragging || this.committed || this.editSessionPending || this.mapStale || !this.mask) return;
    // Hover previews what a single-point probe here would produce, without
    // committing anything — same result the click commits.
    const seed = this.eventToMaskPx(event);
    if (!seed) {
      this.clearPreview();
      return;
    }
    this.runProbe([seed]);
  }

  public onDragStart(data: DragEventData): void {
    if (this.roomEditing) {
      super.onDragStart(data);
      return;
    }
    if (data.button !== 0 || this.editSessionPending || this.mapStale || !this.mask) return;
    // Seed from the PRESS position, not the current cursor: BaseTool only
    // reports a drag once the cursor has moved past its threshold, so
    // `data.event` already sits a handful of px past where the user
    // started — the sweep's first probe belongs exactly where they
    // pressed.
    const seed = this.screenToMaskPx(data.startScreen);
    if (!seed) return;
    // A new sweep ADDS to a frozen overview (runProbe folds the committed
    // fans into every preview), so nothing is cleared here.
    this.dragging = true;
    this.dragPath = [seed];
    const current = this.eventToMaskPx(data.event);
    if (current) this.dragPath.push(current);
  }

  public onDrag(data: DragEventData): void {
    if (this.roomEditing) {
      super.onDrag(data);
      return;
    }
    if (!this.dragging) return;
    const p = this.eventToMaskPx(data.event);
    if (!p) return;
    this.dragPath.push(p);
    // Probe live from the thinned path so the shape grows as the cursor
    // sweeps — the whole point of the drag being a "probe" and not a box.
    this.lastDragOrigins = thinPath(this.dragPath, PROBE_SPACING_PX);
    this.runProbe(this.lastDragOrigins);
  }

  public onDragEnd(data: DragEventData): void {
    if (this.roomEditing) {
      super.onDragEnd(data);
      this.syncRoomEditState();
      return;
    }
    if (!this.dragging) return;
    this.dragging = false;
    // The same release also fires onMouseUp — keep it from committing an
    // extra single-point probe on top of this sweep.
    this.dragJustEnded = true;
    // Commit EXACTLY the origins of the last preview — no extra endpoint,
    // no re-thinning. What the user saw at release is what confirms;
    // recomputing with a shifted origin set could resolve differently.
    const origins = this.lastDragOrigins.length > 0 ? this.lastDragOrigins : thinPath(this.dragPath, PROBE_SPACING_PX);
    this.dragPath = [];
    this.lastDragOrigins = [];
    if (origins.length === 0) return;
    this.commitProbe(origins);
  }

  public onMouseUp(event: MouseEvent): void {
    if (this.roomEditing) {
      super.onMouseUp(event);
      this.syncRoomEditState();
      return;
    }
    if (this.dragJustEnded) {
      this.dragJustEnded = false;
      return;
    }
    // A click that never becomes a drag: BaseTool only reports drags past a
    // 5px threshold, so the single-point probe is committed on mouse UP
    // when no drag ran. On a frozen overview this ADDS a probe.
    if (event.button !== 0 || this.dragging || this.editSessionPending || this.mapStale) return;
    const seed = this.eventToMaskPx(event);
    if (!seed) return;
    this.commitProbe([seed]);
  }

  public onRightClick(event: MouseEvent): void {
    if (this.roomEditing) {
      super.onRightClick(event);
      this.syncRoomEditState();
      return;
    }
    // Right-click ON a committed probe origin removes just that probe and
    // rebuilds the sweep from the rest; the last one clears the overview.
    if (!this.committed || this.committedProbes.length === 0) return;
    const at = this.eventToMaskPx(event);
    if (!at) return;
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < this.committedProbes.length; i++) {
      const origin = this.worldToMaskPx(this.committedProbes[i].originWorld);
      const dist = Math.hypot(origin.x - at.x, origin.y - at.y);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    if (best < 0 || bestDist > PROBE_PICK_PX) return;
    this.committedProbes.splice(best, 1);
    if (this.committedProbes.length === 0) {
      this.reset();
      return;
    }
    this.runProbe([]);
    this.renderOriginMarkers();
  }

  // Click / double-click only matter to the hosted subtool (vertex ops,
  // shape confirm) — the mixin routes them when a session is active;
  // afterwards check whether that ended the session.
  public onClick(event: MouseEvent): void {
    super.onClick(event);
    this.syncRoomEditState();
  }

  public onDoubleClick(event: MouseEvent): void {
    super.onDoubleClick(event);
    this.syncRoomEditState();
  }

  public onKeyDown(event: KeyboardEvent): void {
    if (this.roomEditing) {
      // Enter/Esc go to the mode — it owns the ordering (merge a pending
      // part first, confirm the shape last); the room is created via the
      // adapter's commitNew.
      super.onKeyDown(event);
      this.syncRoomEditState();
      return;
    }
    if (event.key === 'Escape') {
      this.reset();
      return;
    }
    // Enter on a frozen overview: proceed into the edit session with the
    // committed ring (enterEditSession cleans it, same as the direct
    // path). Not while the map is stale — the ring's pixel space is being
    // rebuilt.
    if (event.key === 'Enter' && this.committed && !this.editSessionPending && !this.mapStale && this.lastRawRing && this.lastRawRing.length >= 3) {
      this.startEditSession(this.lastRawRing);
    }
  }

  // ─── Probing ───────────────────────────────────────────────────────────

  /** World → current-capture mask px. */
  private worldToMaskPx(world: THREE.Vector3): TracerPoint {
    const mask = this.mask;
    const uv = this.coordinateTransformer.worldToMaskSpace(world);
    return { x: uv.x * (mask?.width ?? 1), y: uv.y * (mask?.height ?? 1) };
  }

  /**
   * Re-express a committed probe's fan in the CURRENT capture's pixel
   * space. No ray is re-cast — the stored world geometry is converted, so
   * probes taken in earlier viewports keep contributing even when their
   * walls now sit off-screen (out-of-range px coordinates are fine: ring
   * building is pure geometry and never reads the mask).
   */
  private fanToMaskPx(probe: CommittedProbe): ProbeFan {
    const origin = this.worldToMaskPx(probe.originWorld);
    const rays: RayHit[] = probe.raysWorld.map((ray) => {
      const point = this.worldToMaskPx(ray.end);
      return {
        angle: Math.atan2(point.y - origin.y, point.x - origin.x),
        radius: Math.hypot(point.x - origin.x, point.y - origin.y),
        point,
        origin,
        rejected: ray.rejected,
      };
    });
    const points = probe.hitsWorld.map((hit, i): ProbeHit => {
      const p = this.worldToMaskPx(hit);
      return probe.hitsWeak[i] ? { x: p.x, y: p.y, weak: true } : p;
    });
    return { origin, rays, points };
  }

  /**
   * Cast fresh fans for `newOrigins`, with the probes' exclusion disks
   * stamped out of the mask and the thin-occluder carve applied: the first
   * pass casts on the stamped working mask, then `carveThinOccluders` uses
   * ALL fans' rays (committed ones included) to erase thin objects the
   * sweep sees around — open doors especially — and the fresh fans are
   * RE-CAST on the carved mask so their rays reach the wall behind.
   * Committed fans are never re-cast (their hits were carved at their own
   * commit time); they contribute their free space as evidence.
   *
   * Returns the committed + fresh fans combined, and the fresh ones alone
   * (what a commit stores). Hover, drag, commit and rebuild all go through
   * here, so preview == commit.
   */
  private castCombinedFans(newOrigins: TracerPoint[], newRadiiPx?: number[]): { all: ProbeFan[]; fresh: ProbeFan[] } {
    const mask = this.mask as TracerMask;
    const committedFans = this.committedProbes.map((probe) => this.fanToMaskPx(probe));

    // Exclusion disks: no hit is ever taken inside one. They are stamped
    // BLACK into the cast mask, so rays fly through the disk and stop on
    // whatever is behind — placing a probe on a door and growing its ring
    // paints the door out of the sweep.
    const disks = this.exclusionDisks(newOrigins, newRadiiPx);
    const castMask = this.stampExclusions(mask, disks);
    let fresh = newOrigins.map((origin) => probeFan(castMask, origin, this.probeOptions, this.openingHitReject ?? undefined));

    const previousCarved = this.lastCarved.length;
    this.lastCarved = [];
    if (this.probeOptions.thinCarvePx > 0 && committedFans.length + fresh.length > 0) {
      const carve = carveThinOccluders(castMask, [...committedFans, ...fresh], this.probeOptions);
      if (carve.carvedIndices.length > 0) {
        this.lastCarved = carve.carvedIndices;
        fresh = newOrigins.map((origin) => probeFan(carve.mask, origin, this.probeOptions, this.openingHitReject ?? undefined));
      }
    }
    // The overlay bake allocates a full-size ImageData — only redo it when
    // the carve actually changed (count is a good-enough proxy per run).
    if (this.lastCarved.length !== previousCarved) this.rebuildCarveOverlay();

    // STORED hits are re-judged against today's evidence, not the evidence
    // of their commit day:
    //  - a disk grown after its neighbours committed can cover hits they
    //    took earlier — drop any committed hit now inside a disk (fresh
    //    fans were cast on the stamped mask, so theirs are already clean);
    //  - a thin occluder is only carved once the sweep has seen BOTH its
    //    sides. The probe that was committed first saw one side, could not
    //    prove it thin, and stored hits ON it. When later probes complete
    //    the evidence and the carve erases the stroke, those stored hits
    //    must fall with it — otherwise the boundary wraps a loop around a
    //    line the mask no longer even contains.
    const carvedSet = new Set<number>(this.lastCarved);
    const maskWidth = mask.width;
    const nearCarved = (p: TracerPoint): boolean => {
      if (carvedSet.size === 0) return false;
      const px = Math.round(p.x);
      const py = Math.round(p.y);
      for (let dy = -2; dy <= 2; dy++) {
        for (let dx = -2; dx <= 2; dx++) {
          if (carvedSet.has((py + dy) * maskWidth + (px + dx))) return true;
        }
      }
      return false;
    };
    const inDisk = (p: TracerPoint): boolean => disks.some((d) => (p.x - d.x) ** 2 + (p.y - d.y) ** 2 <= d.r * d.r);
    const committedClean = committedFans.map((fan) => ({ ...fan, points: fan.points.filter((p) => !inDisk(p) && !nearCarved(p)) }));

    return { all: [...committedClean, ...fresh], fresh };
  }

  /**
   * The exclusion disks (mask px) of every probe: committed radii are
   * world-anchored (zoom-proof); fresh origins get the default, unless the
   * caller passes their own radii (radius reapply re-probes with each
   * probe's preserved ring).
   */
  private exclusionDisks(newOrigins: TracerPoint[], newRadiiPx?: number[]): Array<{ x: number; y: number; r: number }> {
    const disks = this.committedProbes.map((probe) => {
      const p = this.worldToMaskPx(probe.originWorld);
      return { x: p.x, y: p.y, r: this.pxRadiusFromWorld(probe.exclusionRadiusWorld) };
    });
    newOrigins.forEach((origin, i) => {
      disks.push({ x: origin.x, y: origin.y, r: newRadiiPx?.[i] ?? PROBE_EXCLUSION_PX });
    });
    return disks.filter((d) => d.r > 0);
  }

  /** Stamp the exclusion disks black into a COPY of the mask. */
  private stampExclusions(mask: TracerMask, disks: Array<{ x: number; y: number; r: number }>): TracerMask {
    if (disks.length === 0) return mask;
    const { width, height } = mask;
    const data = new Uint8ClampedArray(mask.data);
    for (const disk of disks) {
      const x0 = Math.max(0, Math.round(disk.x - disk.r));
      const x1 = Math.min(width - 1, Math.round(disk.x + disk.r));
      const y0 = Math.max(0, Math.round(disk.y - disk.r));
      const y1 = Math.min(height - 1, Math.round(disk.y + disk.r));
      const r2 = disk.r * disk.r;
      for (let y = y0; y <= y1; y++) {
        for (let x = x0; x <= x1; x++) {
          if ((x - disk.x) ** 2 + (y - disk.y) ** 2 > r2) continue;
          const i = (y * width + x) * 4;
          data[i] = 0;
          data[i + 1] = 0;
          data[i + 2] = 0;
        }
      }
    }
    return { data, width, height };
  }

  /**
   * Probe from `newOrigins` against the current capture and render the
   * COMBINED result: fresh fans plus every committed probe's stored fan.
   * Pass no origins to re-render the committed sweep alone (rebuild after
   * a recapture, a removed probe, or a settings change).
   */
  private runProbe(newOrigins: TracerPoint[]): void {
    if (!this.mask) return;
    const fans = this.castCombinedFans(newOrigins).all;
    if (fans.length === 0) return;
    const built = buildRing(fans, this.probeOptions);
    this.lastRays = fans.flatMap((fan) => fan.rays);
    this.lastDiscarded = built.discarded;

    this.lastRawRing = built.polygon;
    if (built.polygon.length < 3) {
      this.clearPreview();
      return;
    }
    // The debug view shows what the CURRENT combined sweep sees.
    this.origins = fans.map((fan) => fan.origin);
    const color = built.polygon.length >= MIN_TRUSTED_HITS ? SHAPE_COLOR : WEAK_COLOR;
    void this.cleanAndRender(built.polygon, color);
  }

  /**
   * Commit the probe from `origins`. With "Enter to confirm" on, the sweep
   * is ADDED to the frozen overview — each origin's fan is probed against
   * the current capture and stored world-anchored, so it survives camera
   * moves un-recast — and Enter opens the edit session. Off goes straight
   * into the edit session.
   */
  private commitProbe(origins: TracerPoint[]): void {
    if (!this.mask || origins.length === 0) return;
    if (this.enterToConfirm) {
      this.storeProbes(origins);
      this.committed = true;
      // ALWAYS re-render the combined overview. A probe click-added to a
      // frozen overview never had a preview (hover previews stop once the
      // overview is committed), so the shape on screen predates it and
      // must be rebuilt now — not whenever a wheel-resize or recapture
      // happens to re-probe. Recomputing is safe for the drag path too:
      // the cleanup pipeline is deterministic (seeded RANSAC), so the
      // just-previewed shape re-renders identically.
      this.runProbe([]);
      this.renderOriginMarkers();
      return;
    }
    // Same contract for the direct-to-edit path: hand the session the
    // previewed CLEANED shape when one is on screen, instead of
    // recomputing it.
    if (this.shape && this.shape.length >= 3) {
      this.startEditSession(this.shape, 'draft', true);
      return;
    }
    const { all } = this.castCombinedFans(origins);
    const built = buildRing(all, this.probeOptions);
    if (built.polygon.length < 3) return;
    this.startEditSession(built.polygon);
  }

  /**
   * Probe `origins` through the full two-pass cast (exclusion disks +
   * carve) and store the resulting fans world-anchored. `radiiWorld`
   * preserves per-probe exclusion radii on a re-probe; new probes get the
   * default.
   */
  private storeProbes(origins: TracerPoint[], radiiWorld?: number[]): void {
    const { fresh } = this.castCombinedFans(origins, radiiWorld?.map((radius) => this.pxRadiusFromWorld(radius)));
    fresh.forEach((fan, i) => {
      this.committedProbes.push({
        originWorld: this.maskPxToWorld(fan.origin),
        exclusionRadiusWorld: radiiWorld?.[i] ?? this.worldRadiusFromPx(PROBE_EXCLUSION_PX),
        raysWorld: fan.rays.filter((ray) => ray.point).map((ray) => ({ end: this.maskPxToWorld(ray.point as TracerPoint), rejected: ray.rejected === true })),
        hitsWorld: fan.points.map((hit) => this.maskPxToWorld(hit)),
        hitsWeak: fan.points.map((hit) => hit.weak === true),
      });
    });
  }

  /** Enter the edit session with the double-commit guard (see
   * `editSessionPending`) around the async entry. */
  private startEditSession(ringPx: TracerPoint[], kind: 'draft' | 'test' = 'draft', alreadyCleaned = false): void {
    this.roomSessionKind = kind;
    this.editSessionPending = true;
    void this.enterEditSession(ringPx, alreadyCleaned).finally(() => {
      this.editSessionPending = false;
    });
  }

  /**
   * Hand the probed ring to the hosted LineTool as edge prompts and start
   * the magic-room NEW-shape flow (drag edges/vertices, Enter creates the
   * room via the adapter's commitNew, Esc discards) — the same handoff the
   * wall tracer's `enterPromptSession` does for a traced loop.
   */
  private async enterEditSession(ringPx: TracerPoint[], alreadyCleaned = false): Promise<void> {
    // `alreadyCleaned` hands over the PREVIEWED shape verbatim — cleaning
    // again would let the pipeline's stochastic passes resolve it
    // differently than what the user just saw and confirmed.
    const cleaned = alreadyCleaned ? ringPx : await this.cleanRing(ringPx);
    const points = cleaned.slice();
    if (points.length > 2 && Math.hypot(points[0].x - points[points.length - 1].x, points[0].y - points[points.length - 1].y) < 2) {
      points.pop();
    }
    if (points.length < 3 || this.roomEditing) return;

    // One prompt per polygon edge (midpoint + direction), flattened to the
    // floor — the same construction enterShapeEditMode uses for existing
    // rooms.
    const floorZ = this.currentFloorFloorPlane?.height ?? 0;
    const world = points.map((p) => {
      const w = this.maskPxToWorld(p);
      w.z = floorZ;
      return w;
    });
    const prompts: LinePrompt[] = world.map((a, i) => {
      const b = world[(i + 1) % world.length];
      const midpoint = a.clone().add(b).multiplyScalar(0.5);
      return {
        id: `prompt_${i}`,
        worldPoint: midpoint,
        direction: b.clone().sub(a).normalize(),
        screenPosition: this.lineTool.coordinateTransformer.worldToScreen(midpoint),
      };
    });

    // The session owns the polygon now — clear all probe state + visuals
    // (this also invalidates any in-flight preview render via the token).
    this.reset();
    const mode = this.lineTool.getActiveMode();
    mode.setLinePrompts(prompts);
    mode.updateShapePreview();
    // Capture the snap maps (enterShapeEditMode does this for edit
    // sessions) so density snapping works while dragging the prompts.
    mode.refreshDetection();
    this.setRoomEditRouting(true);
  }

  /**
   * Run the probed ring through the same light polygon pipeline the wall
   * tracer uses (in cm space, so the passes' cm tolerances apply), then
   * render it.
   */
  private async cleanAndRender(ringPx: TracerPoint[], color: number): Promise<void> {
    const token = ++this.previewToken;
    this.lastCleanInput = { ring: ringPx, color };
    const cleaned = await this.cleanRing(ringPx);
    if (token !== this.previewToken) return; // superseded by a newer probe
    this.shape = cleaned;
    this.renderShape(cleaned, color);
    this.renderRays();
    this.renderDebug();
  }

  /** How many current probe origins (mask px) fall outside `ring`, with a
   * small edge tolerance — probes may stand right beside a wall. */
  private countOriginsOutside(ring: TracerPoint[]): number {
    if (ring.length < 3) return this.origins.length;
    const tolerance = this.probeOptions.mergeDistPx * 2;
    let count = 0;
    for (const p of this.origins) {
      let inside = false;
      for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const a = ring[i];
        const b = ring[j];
        if ((a.y > p.y) !== (b.y > p.y) && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
      }
      if (inside) continue;
      let near = Infinity;
      for (let i = 0; i < ring.length; i++) {
        const a = ring[i];
        const b = ring[(i + 1) % ring.length];
        const abx = b.x - a.x;
        const aby = b.y - a.y;
        const l2 = abx * abx + aby * aby;
        const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * abx + (p.y - a.y) * aby) / l2)) : 0;
        near = Math.min(near, Math.hypot(p.x - (a.x + abx * t), p.y - (a.y + aby * t)));
      }
      if (near > tolerance) count++;
    }
    return count;
  }

  private async cleanRing(ringPx: TracerPoint[]): Promise<TracerPoint[]> {
    let cleaned = ringPx;
    const pixelsPerMeter = this.coordinateTransformer.calculatePixelsPerMeter();
    if (pixelsPerMeter && pixelsPerMeter > 0) {
      const pxPerCm = pixelsPerMeter / 100;
      const cmPoints: Point2D[] = ringPx.map((p) => ({ x: p.x / pxPerCm, y: p.y / pxPerCm }));
      try {
        await this.polygonProcessor.waitForPipelineLoad();
        // THE PROBES ARE GROUND TRUTH here too: the pipeline's alignment
        // is stochastic (RANSAC), and an unlucky roll can nudge vertices
        // until a simplify/sharp-angle pass cuts a corner off — leaving a
        // probe outside the cleaned shape the raw ring contained. Retry a
        // few times and take the first result that keeps every probe
        // inside; failing that, keep the best attempt.
        const ATTEMPTS = 3;
        let best: TracerPoint[] | null = null;
        let bestOutside = Infinity;
        for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
          // Clear the pipeline's point cache: it snaps output onto points
          // from PREVIOUS runs for temporal stability, which here would
          // let every hover position contaminate the committed shape.
          // Each probe must be independent, so preview == commit.
          this.polygonProcessor.getPipeline().clearPointCaches();
          const processed = await this.polygonProcessor.getPipeline().process([createPolygon(cmPoints)], { simplify: true });
          if (!processed[0] || processed[0].points.length < 3) continue;
          const mapped = processed[0].points.map((p) => ({ x: p.x * pxPerCm, y: p.y * pxPerCm }));
          // A pass can emit NaN on a degenerate input; those would poison
          // the rendered geometry. Only accept a result that survives.
          const finite = mapped.filter((p) => Number.isFinite(p.x) && Number.isFinite(p.y));
          if (finite.length < 3) continue;
          const outside = this.countOriginsOutside(finite);
          if (outside < bestOutside) {
            bestOutside = outside;
            best = finite;
          }
          if (outside === 0) break;
        }
        if (best) cleaned = best;
      } catch {
        // Pipeline unavailable — keep the raw ring.
      }
    }
    return cleaned;
  }

  private reset(): void {
    this.previewToken++;
    this.origins = [];
    this.dragPath = [];
    this.dragging = false;
    this.shape = null;
    this.committed = false;
    this.committedProbes = [];
    this.lastRays = [];
    this.lastCarved = [];
    this.carveOverlay = null;
    this.lastDiscarded = [];
    this.lastRawRing = null;
    this.lastCleanInput = null;
    this.clearOriginMarkers();
    this.clearPreview();
    this.renderDebug();
  }

  // ─── Settings (debug panel) ────────────────────────────────────────────

  /**
   * Apply the JSON settings config over the compiled defaults. A missing
   * file or missing keys are fine — the defaults hold. Loaded once from
   * the constructor; the fetch can land after onEnable's first capture,
   * so an already-enabled tool refreshes its debug controls and map to
   * the loaded values.
   */
  private async loadSettingsConfig(): Promise<void> {
    const file = await this.debugKit.loadConfig<LidarProbeSettingsFile>();
    if (!file) return;
    this.prep = { ...this.prep, ...(file.prep ?? {}) };
    this.probeOptions = { ...this.probeOptions, ...(file.probe ?? {}) };
    if (typeof file.postProcess === 'boolean') this.postProcess = file.postProcess;
    if (typeof file.enterToConfirm === 'boolean') this.enterToConfirm = file.enterToConfirm;
    if (typeof file.showRays === 'boolean') this.showRays = file.showRays;
    this.refreshSettingsControls();
    if (this.enabled && this.mask) this.captureView();
  }

  /** Download the CURRENT settings as the JSON config file — drop it into
   * apps/client/public/config/segmentation/ to make the tuned values the
   * new baseline (the fixture harness reads the same file). */
  private exportSettings(): void {
    const file: LidarProbeSettingsFile = {
      prep: { ...this.prep },
      probe: { ...this.probeOptions },
      postProcess: this.postProcess,
      enterToConfirm: this.enterToConfirm,
      showRays: this.showRays,
    };
    this.debugKit.exportConfig(file);
  }

  /** Rebuild the debug-panel settings controls from the current values
   * (after the settings config loads over an already-built panel). */
  private refreshSettingsControls(): void {
    if (!this.settingsControlsRoot) return;
    const fresh = this.buildSettingsControls();
    this.settingsControlsRoot.replaceWith(fresh);
    this.settingsControlsRoot = fresh;
  }

  /**
   * Single entry point for every tunable — the debug-panel controls call
   * this. Routes each key to the cheapest refresh that makes it visible:
   * capture-baked parameters recapture (which also rebuilds the debug
   * base, the stage images and, when frozen, the committed ring), the
   * blob filter re-punches, probe parameters re-probe what is on screen.
   */
  private applySetting(key: string, value: number | boolean): void {
    if (key === 'enterToConfirm') {
      // Only affects what a future commit does — nothing to re-probe.
      this.enterToConfirm = Boolean(value);
      return;
    }
    if (key === 'showRays') {
      this.showRays = Boolean(value);
      this.renderRays();
      return;
    }

    if (key === 'sealOpenings') this.prep.sealOpenings = Boolean(value);
    if (key === 'wallDepthValue') this.prep.wallDepthValue = Number(value);
    if (key === 'wallDepthTolerance') this.prep.wallDepthTolerance = Number(value);
    if (key === 'doorDepthMin') this.prep.doorDepthMin = Number(value);
    if (key === 'doorDepthMax') this.prep.doorDepthMax = Number(value);
    if (key === 'closeRadiusPx') this.prep.closeRadiusPx = Number(value);
    if (key === 'skeletonize') this.prep.skeletonize = Boolean(value);
    if (key === 'spurMaxPx') this.prep.spurMaxPx = Number(value);
    if (key === 'holeFillPx') this.prep.holeFillPx = Number(value);
    if (key === 'postProcess') this.postProcess = Boolean(value);
    // The clip threshold is capture-baked too: the skeleton/shell
    // reduction reads it when deciding what is structure.
    if (key === 'clipMin') this.probeOptions.clipMin = Number(value);
    if (CAPTURE_BAKED_SETTINGS.has(key)) {
      this.captureView();
      return;
    }

    if (key === 'minBlobPx') {
      this.probeOptions.minBlobPx = Number(value);
      // The filter is baked into the working mask, so re-punch (which
      // re-bakes the debug view too).
      this.applyRoomPunches();
    }
    if (key === 'rayCount') this.probeOptions.rayCount = Number(value);
    if (key === 'outlierRatio') this.probeOptions.outlierRatio = Number(value);
    if (key === 'maxBridgeRays') this.probeOptions.maxBridgeRays = Number(value);
    if (key === 'mergeDistPx') this.probeOptions.mergeDistPx = Number(value);
    if (key === 'thinCarvePx') this.probeOptions.thinCarvePx = Number(value);
    if (key === 'dropIslands') this.probeOptions.dropIslands = Boolean(value);
    // Any probe parameter changed — refresh what's on screen. A frozen
    // overview only REBUILDS its ring from the stored fans (merge/island/
    // interior params apply; ray-level params can't reach probes whose
    // walls are off-screen, so they only affect future probes). A plain
    // hover preview re-probes its origins outright.
    if (this.committed) {
      this.runProbe([]);
    } else if (this.origins.length > 0) {
      this.runProbe(this.origins);
    }
  }

  // ─── Debug widget ──────────────────────────────────────────────────────

  /**
   * Start a fixture capture — the wall tracer's flow: snapshot the WORKING
   * mask, probe origins, options and position map, then open the current
   * ring in a TEST edit session so the EXPECTED polygon can be corrected
   * by hand. Enter saves the fixture WITH the corrected expected (the
   * adapter's commitNew is intercepted); Esc saves it without. When there
   * is no ring to correct, the fixture is written immediately,
   * invariants-only.
   */
  private saveProbeTest(): void {
    if (!this.mask || this.roomEditing) return;
    const origins = this.committedProbes.length > 0 ? this.committedProbes.map((probe) => this.worldToMaskPx(probe.originWorld)) : this.origins.slice();
    if (origins.length === 0) return;
    this.testSnapshot = {
      origins: origins.map((p) => ({ x: p.x, y: p.y })),
      mask: this.mask,
      raw: this.rawCapture,
      positionMap: this.lastPositionMap,
      pixelsPerMeter: this.coordinateTransformer.calculatePixelsPerMeter() ?? null,
      prep: this.maskPrep(),
    };

    const ring = this.lastRawRing;
    if (!ring || ring.length < 3) {
      this.writeProbeFixture(null);
      this.testSnapshot = null;
      return;
    }
    // Swallow the session's final shape confirm: the polygon becomes the
    // fixture's expected result instead of a new room. Intermediate Enter
    // confirms (merging a new part) never reach commitNew, so they keep
    // working exactly as in the draft flow.
    this.roomAdapter.commitInterceptor = this.handleTestCommit;
    this.startEditSession(ring, 'test');
  }

  /** The test session's shape confirm: save the fixture WITH expected. */
  private handleTestCommit = (polygonWorld: THREE.Vector3[]): void => {
    this.roomAdapter.commitInterceptor = null;
    const snapshot = this.testSnapshot;
    this.testSnapshot = null;
    if (!snapshot) return;
    const expected = polygonWorld.map((world) => {
      const uv = this.coordinateTransformer.worldToMaskSpace(world);
      return { x: uv.x * snapshot.mask.width, y: uv.y * snapshot.mask.height };
    });
    this.writeProbeFixture(expected, snapshot);
  };

  /**
   * Download the snapshotted fixture: the working mask (PNG), the position
   * map (raw .bin — pixel alignment needs it, and a canvas PNG would
   * corrupt it because the 12-bit XY low bits live in alpha, which PNG
   * export premultiplies), and the meta JSON, all sharing one timestamped
   * name. Consumed by runLidarProbeFixtures.mjs. (Cross-viewport sweeps
   * flatten to the snapshotted capture — fixtures replay as a
   * single-capture probe.)
   */
  private writeProbeFixture(expected: TracerPoint[] | null, snapshot = this.testSnapshot): void {
    if (!snapshot) return;
    const { mask, raw, origins, positionMap } = snapshot;
    const stamp = new Date().toISOString().replace(/[:.]/g, '-');
    const { width, height } = mask;

    // Force alpha opaque: canvas PNG encoding premultiplies, so alpha-0
    // pixels would lose their RGB (the probe only reads luminance).
    const savePng = (source: TracerMask, filename: string): void => {
      const rgba = new Uint8ClampedArray(source.data);
      for (let i = 3; i < rgba.length; i += 4) rgba[i] = 255;
      const canvas = document.createElement('canvas');
      canvas.width = source.width;
      canvas.height = source.height;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      ctx.putImageData(new ImageData(rgba, source.width, source.height), 0, 0);
      canvas.toBlob((blob) => {
        if (blob) downloadFile(URL.createObjectURL(blob), filename);
      }, 'image/png');
    };

    // The RAW capture only — the harness recomposes the working map from
    // it with the current prep defaults, so every prep parameter stays
    // tunable against the fixture. The composed map is NOT saved (it
    // would freeze prep at capture time); only if the raw layer is
    // missing does the composed map go out as a legacy fallback.
    if (raw && raw.width === width && raw.height === height) {
      savePng(raw, `lidar-probe-test-${stamp}-raw.png`);
    } else {
      savePng(mask, `lidar-probe-test-${stamp}.png`);
    }

    if (positionMap && positionMap.width === width && positionMap.height === height) {
      const bytes = new Uint8Array(positionMap.data.buffer.slice(0));
      downloadFile(URL.createObjectURL(new Blob([bytes], { type: 'application/octet-stream' })), `lidar-probe-test-${stamp}-posmap.bin`);
    }

    const meta = {
      origins,
      options: { ...this.probeOptions },
      // Record of the prep parameters the capture was taken with — the
      // harness recomposes with the CURRENT defaults by design (that is
      // what makes them tunable); this is provenance, not replay input.
      prep: { ...snapshot.prep },
      width,
      height,
      // The polygon pipeline's tolerances are cm-based — replays need the
      // capture scale to clean the ring exactly like the tool did.
      pixelsPerMeter: snapshot.pixelsPerMeter,
      // Hand-corrected target polygon (mask px), or null for an
      // invariants-only fixture.
      expected: expected?.map((p) => ({ x: p.x, y: p.y })) ?? null,
      createdAt: new Date().toISOString(),
    };
    downloadFile(URL.createObjectURL(new Blob([JSON.stringify(meta, null, 2)], { type: 'application/json' })), `lidar-probe-test-${stamp}.json`);
  }

  private createDebugWidget(): void {
    if (this.debugContainer || !this.viewer.debugOverlay) return;

    const container = document.createElement('div');
    container.style.cssText = `margin-top: 8px;`;

    const title = document.createElement('div');
    title.style.cssText = `font-size: 12px; font-weight: bold; color: rgba(255,255,255,0.9); margin-bottom: 4px;`;
    title.textContent = 'LiDAR Probe (thresholded map + probe):';
    container.appendChild(title);

    this.debugCanvas = document.createElement('canvas');
    this.debugCanvas.style.cssText = `width: 100%; max-width: 100%; border: 2px solid #00e5c3; border-radius: 4px; background: #000; display: block;`;
    container.appendChild(this.debugCanvas);

    // Fixture capture: downloads the CURRENT composed map (PNG) plus the
    // active probe origins + options (JSON) as a reproducible test case
    // for runLidarProbeFixtures.mjs — same workflow as the wall tracer.
    const testButton = document.createElement('button');
    testButton.style.cssText = `margin: 6px 0; padding: 4px 8px; font-size: 12px; cursor: pointer; border-radius: 4px; border: 1px solid #00e5c3; background: transparent; color: #00e5c3; display: block; width: 100%;`;
    testButton.textContent = 'Create test (correct expected, Enter saves / Esc saves without)';
    testButton.onclick = () => this.saveProbeTest();
    container.appendChild(testButton);

    const legend = document.createElement('div');
    legend.style.cssText = `display: flex; gap: 10px; margin: 4px 0; font-size: 11px; color: rgba(255,255,255,0.85); flex-wrap: wrap;`;
    const addLegend = (label: string, color: string): void => {
      const wrap = document.createElement('span');
      wrap.style.cssText = `display: flex; align-items: center; gap: 3px;`;
      const swatch = document.createElement('span');
      swatch.style.cssText = `width: 9px; height: 9px; border-radius: 2px; background: ${color};`;
      wrap.append(swatch, document.createTextNode(label));
      legend.appendChild(wrap);
    };
    addLegend('probed ring', 'rgba(255,64,64,0.9)');
    addLegend('cleaned', 'rgba(0,229,195,0.9)');
    addLegend('discarded rays', '#ff4444');
    addLegend('discarded hits', '#ff33cc');
    addLegend('carved thin objects', '#ff9628');
    addLegend('sealed openings', '#40c8ff');
    addLegend('probes', '#ffffff');
    container.appendChild(legend);

    // Intermediate composition stages — one image per pipeline step, so
    // every parameter can be judged against the stage it shapes. Click
    // any image for the fullscreen pixel inspector.
    this.debugKit.attachStageStrip(container);

    this.settingsControlsRoot = this.buildSettingsControls();
    container.appendChild(this.settingsControlsRoot);

    // Export the CURRENT settings as the JSON config — drop the file into
    // apps/client/public/config/segmentation/ to make the tuned values
    // the new baseline the tool (and the fixture harness) load.
    this.debugKit.exportButton(container, () => this.exportSettings());

    // Any pipeline edit (toggle/params/add/reorder) re-cleans the current
    // ring immediately, so the shape on screen always reflects the active
    // passes — same live behaviour as the wall tracer's panel.
    this.pipelinePanel = new PipelineControlPanel({
      onPipelineChanged: () => {
        if (this.lastCleanInput) void this.cleanAndRender(this.lastCleanInput.ring, this.lastCleanInput.color);
      },
    });
    this.pipelinePanel.addTab({
      id: 'lidarProbePolygon',
      label: 'Probe Cleanup',
      pipeline: this.polygonProcessor.getPipeline() as unknown as PipelineInterface,
    });
    container.appendChild(this.pipelinePanel.getElement());

    this.debugContainer = container;
    this.viewer.debugOverlay.addWidget({
      name: 'lidar-probe',
      container,
      update: () => this.pipelinePanel?.refreshTimings(),
      destroy: () => {
        if (container.parentElement) container.parentElement.removeChild(container);
      },
    });
    this.rebuildDebugBase();
  }

  private destroyDebugWidget(): void {
    if (!this.debugContainer) return;
    this.viewer.debugOverlay?.removeWidget('lidar-probe');
    this.debugContainer = null;
    this.debugCanvas = null;
    this.debugBase = null;
    this.pipelinePanel = null;
    this.debugKit.detach();
    this.settingsControlsRoot = null;
    this.lastRawRing = null;
    this.lastCleanInput = null;
  }

  /**
   * All tool settings, organized by pipeline stage. The post-processing
   * section's heading carries the master toggle for every step after the
   * clip threshold (skeleton/shell reduction, spur pruning, small-blob
   * removal) — off casts against the plain closed map, the A/B view for
   * judging the steps against the stage images.
   */
  private buildSettingsControls(): HTMLElement {
    const root = document.createElement('div');

    let body = this.debugKit.section(root, 'Capture filters');
    this.debugKit.slider(body, 'Wall depth', 0, 255, 1, this.prep.wallDepthValue, (v) => this.applySetting('wallDepthValue', v));
    this.debugKit.slider(body, 'Wall depth tolerance (128 = off)', 0, 128, 1, this.prep.wallDepthTolerance, (v) => this.applySetting('wallDepthTolerance', v));
    this.debugKit.band(body, 'Door depth band', 0, 255, this.prep.doorDepthMin, this.prep.doorDepthMax, (lo, hi) => {
      this.prep.doorDepthMin = lo;
      this.prep.doorDepthMax = hi;
      this.captureView();
    });
    this.debugKit.toggle(body, 'Opening zones', this.prep.sealOpenings, (v) => this.applySetting('sealOpenings', v));
    this.debugKit.slider(body, 'Close radius', 0, 40, 1, this.prep.closeRadiusPx, (v) => this.applySetting('closeRadiusPx', v));

    body = this.debugKit.section(root, 'Clipping');
    this.debugKit.slider(body, 'Wall threshold', 0, 255, 5, this.probeOptions.clipMin, (v) => this.applySetting('clipMin', v));

    // Post-processing: heading with the master toggle; the body dims and
    // mutes while it is off, like the settings panel's categories.
    const ppBody = this.debugKit.toggleSection(root, 'Post-processing', this.postProcess, (v) => this.applySetting('postProcess', v));
    this.debugKit.toggle(ppBody, 'Skeletonize (off = shell hollow)', this.prep.skeletonize, (v) => this.applySetting('skeletonize', v));
    this.debugKit.slider(ppBody, 'Spur prune length', 0, 60, 1, this.prep.spurMaxPx, (v) => this.applySetting('spurMaxPx', v));
    this.debugKit.slider(ppBody, 'Fill holes up to (px)', 0, 10000, 100, this.prep.holeFillPx, (v) => this.applySetting('holeFillPx', v));
    this.debugKit.slider(ppBody, 'Min object size', 0, 20000, 100, this.probeOptions.minBlobPx, (v) => this.applySetting('minBlobPx', v));

    body = this.debugKit.section(root, 'Probing');
    this.debugKit.slider(body, 'Rays per probe', 60, 720, 30, this.probeOptions.rayCount, (v) => this.applySetting('rayCount', v));
    this.debugKit.slider(body, 'Escape tolerance', 1.05, 3, 0.05, this.probeOptions.outlierRatio, (v) => this.applySetting('outlierRatio', v));
    this.debugKit.slider(body, 'Max opening width (rays)', 2, 180, 2, this.probeOptions.maxBridgeRays, (v) => this.applySetting('maxBridgeRays', v));
    this.debugKit.slider(body, 'Merge distance', 1, 40, 1, this.probeOptions.mergeDistPx, (v) => this.applySetting('mergeDistPx', v));
    this.debugKit.slider(body, 'Carve thin objects', 0, 30, 1, this.probeOptions.thinCarvePx, (v) => this.applySetting('thinCarvePx', v));
    this.debugKit.toggle(body, 'Drop islands', this.probeOptions.dropIslands, (v) => this.applySetting('dropIslands', v));

    body = this.debugKit.section(root, 'Display');
    this.debugKit.toggle(body, 'Show rays', this.showRays, (v) => this.applySetting('showRays', v));
    this.debugKit.toggle(body, 'Enter to confirm', this.enterToConfirm, (v) => this.applySetting('enterToConfirm', v));

    return root;
  }

  /**
   * Bake the map AS THE RAYS SEE IT: everything below the probe threshold
   * knocked to black. This is the whole point of the debug view — the
   * tracer's map at the tracer's threshold looks quite different from the
   * same map at the probe's higher one, and which walls survive that cut is
   * what decides where the rays stop.
   */
  private rebuildDebugBase(): void {
    if (!this.mask || !this.debugContainer) return;
    // this.mask already has its small blobs erased (baked in applyRoomPunches),
    // so thresholding it here shows exactly what the rays read.
    const { data, width, height } = this.mask;
    const base = document.createElement('canvas');
    base.width = width;
    base.height = height;
    const ctx = base.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(width, height);
    for (let i = 0; i < width * height; i++) {
      const lum = (data[i * 4] + data[i * 4 + 1] + data[i * 4 + 2]) / 3;
      const v = lum >= this.probeOptions.clipMin ? lum : 0;
      img.data[i * 4] = v;
      img.data[i * 4 + 1] = v;
      img.data[i * 4 + 2] = v;
      img.data[i * 4 + 3] = 255;
    }
    ctx.putImageData(img, 0, 0);
    this.debugBase = base;
    this.renderDebug();
  }

  /** Bake the carved-pixel overlay (orange) for the debug canvas. */
  private rebuildCarveOverlay(): void {
    this.carveOverlay = null;
    if (this.lastCarved.length === 0 || !this.mask) return;
    const { width, height } = this.mask;
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const img = ctx.createImageData(width, height);
    for (const i of this.lastCarved) {
      img.data[i * 4] = 255;
      img.data[i * 4 + 1] = 150;
      img.data[i * 4 + 2] = 40;
      img.data[i * 4 + 3] = 210;
    }
    ctx.putImageData(img, 0, 0);
    this.carveOverlay = canvas;
  }

  /** Thresholded map + discarded rays + probed ring + cleaned polygon. */
  private renderDebug(): void {
    if (!this.debugCanvas || !this.debugBase) return;
    const canvas = this.debugCanvas;
    canvas.width = this.debugBase.width;
    canvas.height = this.debugBase.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(this.debugBase, 0, 0);
    // Thin objects the carve erased before casting — if a real wall ever
    // shows orange here, the carve radius is too aggressive.
    if (this.carveOverlay) ctx.drawImage(this.carveOverlay, 0, 0);
    // Sealed door/window openings from the position-map depth band — blue.
    if (this.sealOverlay) ctx.drawImage(this.sealOverlay, 0, 0);

    // Discarded rays first, underneath the shape: the red fans pointing
    // through the doorways are the clearest picture of what was rejected.
    ctx.strokeStyle = 'rgba(255, 68, 68, 0.5)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const ray of this.lastRays) {
      if (!ray.rejected || !ray.point) continue;
      ctx.moveTo(ray.origin.x, ray.origin.y);
      ctx.lineTo(ray.point.x, ray.point.y);
    }
    ctx.stroke();

    const drawRing = (ring: TracerPoint[] | null, style: string, lineWidth: number): void => {
      if (!ring || ring.length < 2) return;
      ctx.strokeStyle = style;
      ctx.lineWidth = lineWidth;
      ctx.beginPath();
      ctx.moveTo(ring[0].x, ring[0].y);
      for (let i = 1; i < ring.length; i++) ctx.lineTo(ring[i].x, ring[i].y);
      ctx.closePath();
      ctx.stroke();
    };
    drawRing(this.lastRawRing, 'rgba(255, 64, 64, 0.9)', 2);
    drawRing(this.shape, 'rgba(0, 229, 195, 0.9)', 2);

    // Discarded hits — furniture, islands, interior clutter. Magenta so
    // they read as "deliberately dropped" rather than as boundary; if a
    // whole wall shows up magenta, the island rule is eating real geometry.
    ctx.fillStyle = '#ff33cc';
    for (const p of this.lastDiscarded) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
      ctx.fill();
    }

    // Probe origins — where the cursor actually sampled from.
    ctx.fillStyle = '#ffffff';
    for (const origin of this.origins) {
      ctx.beginPath();
      ctx.arc(origin.x, origin.y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Exclusion rings of the committed probes — the stamped-out disks.
    if (this.mask) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)';
      ctx.lineWidth = 1;
      for (const probe of this.committedProbes) {
        const origin = this.worldToMaskPx(probe.originWorld);
        ctx.beginPath();
        ctx.arc(origin.x, origin.y, this.pxRadiusFromWorld(probe.exclusionRadiusWorld), 0, Math.PI * 2);
        ctx.stroke();
      }
    }
  }

  // ─── Rendering ─────────────────────────────────────────────────────────

  private renderShape(ringPx: TracerPoint[], color: number): void {
    this.clearShapeVisuals();
    if (ringPx.length < 3) return;

    // A trusted shape renders EXACTLY like the magic room tool's polygon
    // preview (green fill + purple outline + vertex dots) — same renderer,
    // same styling. The capture dims + mask corners set in captureView are
    // what its pixel→world mapping reads. Weak shapes keep the orange line:
    // the warning color IS the feedback that too few hits survived.
    if (color === SHAPE_COLOR) {
      this.polygonRenderer.renderPolygons([createPolygon(ringPx.map((p) => ({ x: p.x, y: p.y })))], null);
      return;
    }

    const positions: number[] = [];
    for (const p of ringPx) {
      const world = this.maskPxToWorld(p);
      positions.push(world.x, world.y, world.z);
    }
    // Close the ring visually — the probed boundary is always a loop.
    positions.push(positions[0], positions[1], positions[2]);

    this.previewGeometry = new LineGeometry();
    this.previewGeometry.setPositions(positions);
    this.previewMaterial = new LineMaterial({
      color,
      linewidth: 3,
      resolution: new THREE.Vector2(this.viewer.renderArea.clientWidth, this.viewer.renderArea.clientHeight),
      depthTest: false,
    });
    this.previewLine = new Line2(this.previewGeometry, this.previewMaterial);
    this.previewLine.renderOrder = 999;
    this.viewer.uiScene.add(this.previewLine);
  }

  /**
   * Debug overlay: every ray as a segment from its origin to where it
   * stopped, red when it was discarded as an escape. This is what makes the
   * doorway rejection legible — the red fans point straight through the
   * openings.
   */
  private renderRays(): void {
    this.clearRayVisuals();
    if (!this.showRays || this.lastRays.length === 0) return;

    const kept: number[] = [];
    const rejected: number[] = [];
    for (const ray of this.lastRays) {
      if (!ray.point) continue;
      const from = this.maskPxToWorld(ray.origin);
      const to = this.maskPxToWorld(ray.point);
      const target = ray.rejected ? rejected : kept;
      target.push(from.x, from.y, from.z, to.x, to.y, to.z);
    }

    const group = new THREE.Group();
    for (const [positions, color] of [
      [kept, KEPT_RAY_COLOR],
      [rejected, REJECTED_RAY_COLOR],
    ] as const) {
      if (positions.length === 0) continue;
      const geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      group.add(new THREE.LineSegments(geometry, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.45, depthTest: false })));
    }
    group.renderOrder = 998;
    this.rayGroup = group;
    this.viewer.uiScene.add(group);
  }

  // ─── Exclusion radii ───────────────────────────────────────────────────

  /** Mask-px ↔ world-meter radius conversions (fallback 100px/m). */
  private worldRadiusFromPx(px: number): number {
    const ppm = this.coordinateTransformer.calculatePixelsPerMeter();
    return ppm && ppm > 0 ? px / ppm : px / 100;
  }

  private pxRadiusFromWorld(world: number): number {
    const ppm = this.coordinateTransformer.calculatePixelsPerMeter();
    return ppm && ppm > 0 ? world * ppm : world * 100;
  }

  /** Flat world-anchored circle showing a probe's exclusion radius. */
  private createRadiusRing(center: THREE.Vector3, radiusWorld: number): THREE.LineLoop {
    const SEGMENTS = 48;
    const positions = new Float32Array(SEGMENTS * 3);
    for (let i = 0; i < SEGMENTS; i++) {
      const a = (i / SEGMENTS) * Math.PI * 2;
      positions[i * 3] = Math.cos(a);
      positions[i * 3 + 1] = Math.sin(a);
    }
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    const material = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.55, depthTest: false });
    const ring = new THREE.LineLoop(geometry, material);
    ring.renderOrder = 998;
    ring.position.copy(center);
    ring.scale.setScalar(radiusWorld);
    this.viewer.uiScene.add(ring);
    return ring;
  }

  private disposeRing(ring: THREE.LineLoop | undefined): void {
    if (!ring) return;
    this.viewer.uiScene.remove(ring);
    ring.geometry.dispose();
    (ring.material as THREE.Material).dispose();
  }

  /**
   * Debounced: re-probe after wheel resizing settles — the ring visual
   * tracks every tick, but re-casting the fans per wheel notch is too
   * heavy. On-screen probes are re-cast so a grown disk actually reveals
   * the wall behind what it erased; off-screen probes keep their stored
   * hits (there is nothing to re-cast them against).
   */
  private scheduleRadiusReapply(): void {
    if (this.radiusReapplyTimer !== null) window.clearTimeout(this.radiusReapplyTimer);
    this.radiusReapplyTimer = window.setTimeout(() => {
      this.radiusReapplyTimer = null;
      if (this.committed) this.reprobeVisibleProbes();
    }, 200);
  }

  /** Re-cast every committed probe that lies inside the current capture
   * through the same two-pass cast a commit uses, preserving each probe's
   * exclusion radius, then rebuild the ring. */
  private reprobeVisibleProbes(): void {
    if (!this.mask) return;
    const mask = this.mask;
    const visible: Array<{ index: number; origin: TracerPoint; radiusWorld: number }> = [];
    this.committedProbes.forEach((probe, index) => {
      const origin = this.worldToMaskPx(probe.originWorld);
      if (origin.x >= 0 && origin.x < mask.width && origin.y >= 0 && origin.y < mask.height) {
        visible.push({ index, origin, radiusWorld: probe.exclusionRadiusWorld });
      }
    });
    if (visible.length > 0) {
      for (let i = visible.length - 1; i >= 0; i--) this.committedProbes.splice(visible[i].index, 1);
      this.storeProbes(visible.map((v) => v.origin), visible.map((v) => v.radiusWorld));
    }
    this.runProbe([]);
    this.renderOriginMarkers();
  }

  public onWheel(event: WheelEvent): void {
    if (this.roomEditing) {
      super.onWheel(event);
      return;
    }
    if (!this.committed || this.committedProbes.length === 0) return;
    const at = this.eventToMaskPx(event);
    if (!at) return;
    // Pick the probe whose dot or exclusion ring is under the cursor.
    let best = -1;
    let bestDist = Infinity;
    for (let i = 0; i < this.committedProbes.length; i++) {
      const origin = this.worldToMaskPx(this.committedProbes[i].originWorld);
      const dist = Math.hypot(origin.x - at.x, origin.y - at.y);
      const pickPx = Math.max(PROBE_PICK_PX, this.pxRadiusFromWorld(this.committedProbes[i].exclusionRadiusWorld));
      if (dist <= pickPx && dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    if (best < 0) return; // not over a probe — let the camera zoom normally

    // Consume the event so orbit zoom stays blocked while resizing.
    event.preventDefault();
    event.stopPropagation();

    const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
    const probe = this.committedProbes[best];
    probe.exclusionRadiusWorld = Math.min(3, Math.max(0.05, probe.exclusionRadiusWorld * factor));
    this.originRings[best]?.scale.setScalar(probe.exclusionRadiusWorld);
    this.scheduleRadiusReapply();
  }

  private renderOriginMarkers(): void {
    this.clearOriginMarkers();
    for (const probe of this.committedProbes) {
      const marker = new SphereMarker(this.viewer);
      marker.position.copy(probe.originWorld);
      this.viewer.uiScene.add(marker);
      this.originMarkers.push(marker);
      this.originRings.push(this.createRadiusRing(probe.originWorld, probe.exclusionRadiusWorld));
    }
  }

  private clearOriginMarkers(): void {
    for (const marker of this.originMarkers) {
      this.viewer.uiScene.remove(marker);
      marker.dispose();
    }
    this.originMarkers = [];
    for (const ring of this.originRings) this.disposeRing(ring);
    this.originRings = [];
  }

  private clearShapeVisuals(): void {
    this.polygonRenderer.clearPolygonRendering();
    if (this.previewLine) {
      this.viewer.uiScene.remove(this.previewLine);
      this.previewLine = null;
    }
    this.previewGeometry?.dispose();
    this.previewGeometry = null;
    this.previewMaterial?.dispose();
    this.previewMaterial = null;
  }

  private clearRayVisuals(): void {
    if (!this.rayGroup) return;
    this.viewer.uiScene.remove(this.rayGroup);
    this.rayGroup.traverse((obj) => {
      const withGeom = obj as THREE.LineSegments;
      withGeom.geometry?.dispose?.();
      (withGeom.material as THREE.Material | undefined)?.dispose?.();
    });
    this.rayGroup = null;
  }

  private clearPreview(): void {
    this.shape = null;
    this.clearShapeVisuals();
    this.clearRayVisuals();
  }
}
